import { useControlledInput } from '@deltasierra/react-hooks';
import { isNullOrUndefined } from '@deltasierra/type-utilities';
import { t } from '@deltasierra/shared';
import { noop } from '@deltasierra/utilities-object';
import * as React from 'react';
import { DSButtonProps, DSButton } from '../../Generic/Buttons/DSButton';
import {
    DSDialogProps,
    DSDialog,
    DSDialogActions,
    DSDialogContent,
    DSDialogContentText,
    DSDialogTitle,
} from '../Dialog';
import { DSTextField } from '../TextField';

export type DSConfirmDialogProps = Omit<DSDialogProps, 'children' | 'onClose'> & {
    /**
     * The label for the cancel button
     *
     * @default 'Cancel'
     */
    cancelText?: string;
    /**
     * The label for the confirm button
     *
     * @default 'Ok'
     */
    confirmText?: string;
    /**
     *
     * @default 'default'
     */
    confirmColor?: DSButtonProps['color'];
    /**
     * If set, will require user to enter text before continuing
     */
    confirmationText?: string;
    dialogTitle?: string;
    /**
     * The message displayed in the modal
     */
    message: JSX.Element | string;
    /**
     * Whether the confirm button is disabled
     */
    dialogConfirmDisabled?: boolean;
    onCancel?: () => void;
    onClose?: () => void;
    onConfirm?: () => void;
};

export const DSConfirmDialog: React.FC<DSConfirmDialogProps> = ({
    cancelText = 'Cancel',
    confirmationText,
    confirmColor = 'primary',
    confirmText = 'Ok',
    dialogConfirmDisabled = false,
    dialogTitle,
    message,
    onCancel = noop,
    onClose = noop,
    onConfirm = noop,
    ...rest
}) => {
    const isConfirmationRequired = !isNullOrUndefined(confirmationText);

    const [confirmationInputText, updateConfirmationInputText, confirmationInputProps] = useControlledInput();

    const onEnter = React.useCallback(() => {
        if (isConfirmationRequired) {
            updateConfirmationInputText('');
        }
    }, [isConfirmationRequired, updateConfirmationInputText]);

    const handleOnCancel = React.useCallback(() => {
        onCancel();
        onClose();
    }, [onCancel, onClose]);

    const handleOnConfirm = React.useCallback(() => {
        onConfirm();
        onClose();
    }, [onConfirm, onClose]);

    return (
        <DSDialog {...rest} TransitionProps={{ onEnter }} onClose={onClose}>
            <DSDialogTitle>{dialogTitle}</DSDialogTitle>
            <DSDialogContent>
                {typeof message === 'string' ? <DSDialogContentText>{message}</DSDialogContentText> : message}
                {isConfirmationRequired && (
                    <>
                        <DSDialogContentText
                            dangerouslySetInnerHTML={{
                                __html: t('COMMON.CONFIRM_MODAL_INPUT', { input: confirmationText }),
                            }}
                        />
                        <DSTextField
                            fullWidth
                            label={confirmationText}
                            placeholder={confirmationText}
                            variant="filled"
                            {...confirmationInputProps}
                        />
                    </>
                )}
            </DSDialogContent>
            <DSDialogActions>
                <DSButton variant="text" onClick={handleOnCancel}>
                    {cancelText}
                </DSButton>
                <DSButton
                    color={confirmColor}
                    disabled={
                        (isConfirmationRequired && confirmationInputText !== confirmationText) || dialogConfirmDisabled
                    }
                    variant="text"
                    onClick={handleOnConfirm}
                >
                    {confirmText}
                </DSButton>
            </DSDialogActions>
        </DSDialog>
    );
};
DSConfirmDialog.displayName = 'DSConfirmDialog';
